import { useMemo, useCallback, useRef } from 'react';

import {
  Box,
  ListItemText,
  TextField,
  Typography,
  Button,
  alpha,
  makeStyles,
  IconButton,
  Slide,
  useTheme,
} from '@material-ui/core';
import { Edit as EditIcon } from '@material-ui/icons';
import BlockIcon from '@material-ui/icons/Block';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';
import { format } from 'date-fns';
import { FormikProvider, useFormik } from 'formik';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { usePhoneInput } from 'react-international-phone';
import { useModal } from 'react-modal-hook';

import { BlockedContactBadge } from 'components/BlockedContactBadge';
import { BlockedContactName } from 'components/BlockedContactName';
import { ContactAvatar } from 'components/ContactAvatar';
import CustomFields from 'components/CustomFields';
import { FormButton } from 'components/FormButton';
import ImageViewer from 'components/ImageViewer';
import ModalAddAgendamento from 'components/ModalAddAgendamento';
import ModalDefinirEquipePadrao from 'components/ModalDefinirEquipePadrao';
import ModalIniciarContato from 'components/ModalIniciarContato';
import TagsV2 from 'components/TagsV2/TagsV2';
import { useAppDispatch, useAppSelector } from 'store';
import {
  atendimentosSelector,
  fetchAtendimentosIniciados,
  fetchFila,
  setAtendimentoSelecionado,
} from 'store/reducers/atendimentos';
import { TipoCanalEnum } from 'store/reducers/canais';
import {
  alterarContatoV2,
  updateListaContatos,
  atualizarContatoV2,
  ContatoInterface,
  ContatoInterfaceV2,
  removerEquipeOuAtendentePadrao,
  CustomFieldsEnum,
} from 'store/reducers/contatos';
import getIconTipoCanal from 'utils/getIconTipoCanal';
import { getUrlParams } from 'utils/getUrlParams';
import { normalizeContato } from 'utils/normalizeContato';
import * as validators from 'utils/validators';

const useStyles = makeStyles(
  ({ zIndex, spacing, appDrawer, palette, typography }) => ({
    backgroundAvatar: ({
      readonlyFields,
    }: TabPanelInformacoesContatoProps) => ({
      position: 'relative',
      width: readonlyFields ? spacing(8) : spacing(10),
      height: readonlyFields ? spacing(8) : spacing(10),
      backgroundColor: alpha(palette.primary.main, 0.2),
      borderRadius: spacing(1),
      borderTopRightRadius: spacing(2),
      borderBottomRightRadius: spacing(2),
      marginRight: spacing(3),
    }),
    avatarBadge: ({ readonlyFields }: TabPanelInformacoesContatoProps) => ({
      position: 'absolute',
      top: 0,
      right: readonlyFields ? -25 : -35,
    }),
    avatarLarge: ({ readonlyFields }: TabPanelInformacoesContatoProps) => ({
      width: readonlyFields ? spacing(8) : spacing(10),
      height: readonlyFields ? spacing(8) : spacing(10),
      border: `2px solid ${alpha(palette.primary.main, 0.3)}`,
    }),
    editableField: {
      '& > :last-child': {
        transition: 'opacity 0.1s ease',
        cursor: 'pointer',
        opacity: 0,
      },
      '&:hover > :last-child': {
        opacity: 1,
      },
    },
    observation: {
      width: '100%',
      minHeight: spacing(10),
      backgroundColor: alpha(palette.action.disabledBackground, 0.05),
      borderRadius: spacing(1),
      padding: '12px 14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',

      '& > :first-child': {
        display: 'flex',
        minHeight: spacing(9),
        flexDirection: 'column',
        gap: spacing(1),
      },
    },
    nameContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing(1),

      '& > .MuiTypography-root': {
        fontWeight: typography.fontWeightMedium as number,
      },
    },
    inputText: {
      '& .MuiInputLabel-root': {
        fontSize: typography.pxToRem(14),
      },

      '& .MuiInputBase-input': {
        fontSize: typography.pxToRem(14),
      },
    },
    formActions: {
      width: appDrawer.minWidth,
      position: 'fixed',
      height: spacing(8),
      backgroundColor: palette.background.default,
      bottom: 0,
      right: 0,
      zIndex: zIndex.appBar + 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: spacing(0, 4),
      gap: spacing(2),
    },
    actionButton: {
      backgroundColor: palette.background.default,
      color: palette.text.primary,

      '&:hover': {
        backgroundColor: alpha(palette.action.hover, 0.1),
      },
    },
    blockedAlert: {
      backgroundColor: alpha(palette.error.main, 0.12),
      border: `1px solid ${palette.error.main}`,
      borderRadius: spacing(1),
      padding: spacing(1.5, 2),
      display: 'flex',
      alignItems: 'center',
      gap: spacing(1),
    },
  }),
);

interface TabPanelInformacoesContatoProps {
  contato: ContatoInterfaceV2 | null;
  isAtendimento?: boolean;
  editMode?: boolean;
  readonlyFields?: boolean;
  onEnableEditMode?: (value: boolean) => void;
  onSuccess?: () => void;
}

interface FormValues {
  name: string;
  observation: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export default function TabPanelInformacoesContato(
  props: TabPanelInformacoesContatoProps,
) {
  const {
    contato,
    isAtendimento = false,
    editMode = false,
    readonlyFields = false,
    onEnableEditMode,
    onSuccess,
  } = props;
  const theme = useTheme();

  const classes = useStyles(props);
  const dispatch = useAppDispatch();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const { atendimentoSelecionado } = useAppSelector(atendimentosSelector);
  const inputRefs = useRef<Record<string, HTMLInputElement>>({});

  const { orderInitiatedBy, orderBy } = getUrlParams();

  const isInstagramOrMessengerChannel =
    contato?.tipoCanal === TipoCanalEnum.INSTAGRAM ||
    contato?.tipoCanal === TipoCanalEnum.MESSENGER;

  const { dynamicInitialValues, dynamicSchema } = useMemo(
    () => validators.buildDynamicFormikSchema(contato?.customFields ?? []),
    [contato?.customFields],
  );

  const fixedInitialValues = useMemo(
    () => ({
      name: contato?.name ?? '',
      observation: contato?.observation ?? '',
    }),
    [contato?.name, contato?.observation],
  );

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      ...fixedInitialValues,
      ...dynamicInitialValues,
    },
    validationSchema: validators.alterarContatoV2Schema
      .pick(Object.keys(fixedInitialValues))
      .concat(dynamicSchema),
    onSubmit: async values => {
      if (!contato) return;

      const fixedFields = Object.keys(fixedInitialValues);

      const customData = Object.entries(values)
        .filter(
          ([key, value]) =>
            !fixedFields.includes(key) && value !== '' && value !== null,
        )
        .map(([id, value]) => {
          if (value instanceof Date && value.toString() !== 'Invalid Date') {
            return {
              id,
              value: format(value, 'yyyy-MM-dd'),
            };
          }
          return {
            id,
            value,
          };
        });

      const payload = {
        contatoId: contato?.id,
        name: formik.values.name,
        observation: formik.values.observation,
        customFields: customData,
      };

      await dispatch(alterarContatoV2(payload))
        .unwrap()
        .then(novoContato => {
          enqueueSnackbar('Contato alterado com sucesso', {
            variant: 'success',
          });
          dispatch(atualizarContatoV2(novoContato));
          dispatch(
            updateListaContatos({
              id: contato.id,
              nome: novoContato.name,
              observacao: novoContato.observation,
            }),
          );

          /**
           * 1º Atualiza os atendimentos iniciados
           * 2º Atualiza a fila de espera
           * 3º Atualiza o atendimento selecionado
           * */
          if (atendimentoSelecionado) {
            dispatch(fetchAtendimentosIniciados({ orderBy: orderInitiatedBy }));
            dispatch(fetchFila({ orderBy }));

            dispatch(
              setAtendimentoSelecionado({
                ...atendimentoSelecionado,
                contato: {
                  ...atendimentoSelecionado.contato,
                  nome: novoContato.name,
                  observacao: novoContato.observation,
                },
              }),
            );
          }

          onSuccess?.();
          onEnableEditMode?.(false);
        })
        .catch(error => {
          if (error.response) enqueueSnackbar(error.response.data.message);
        });
    },
  });

  const { phone } = usePhoneInput({
    defaultCountry: 'br',
    value: contato?.contact ?? '',
  });

  const handleRemoverEquipePadrao = useCallback(() => {
    if (!contato) return;

    confirm({
      title: 'Remover atendente padrão',
      description: `Deseja remover a equipe/atendente padrão atribuido ao contato ${contato.name}?`,
      cancellationText: 'Cancelar',
      confirmationText: 'Remover',
      confirmationButtonProps: { color: 'secondary' },
      dialogProps: { maxWidth: 'xs' },
    })
      .then(() => {
        dispatch(removerEquipeOuAtendentePadrao(contato.id))
          .unwrap()
          .then(novoContato => {
            dispatch(updateListaContatos(novoContato));

            dispatch(
              atualizarContatoV2({
                ...contato,
                teamDefault: null,
                atendenteDefault: null,
              }),
            );

            enqueueSnackbar(
              `O contato ${contato.name} não possui mais uma equipe/atendente vinculado a ele.`,
            );
          })
          .catch(error => {
            if (error.response) enqueueSnackbar(error.response.data.message);
          });
      })
      .catch(() => undefined);
  }, [confirm, contato, dispatch, enqueueSnackbar]);

  const [showModalDefinirEquipePadrao, hideModalDefinirEquipePadrao] = useModal(
    ({ in: open }) => {
      if (!contato) return null;

      return (
        <ModalDefinirEquipePadrao
          open={open}
          contatoId={contato.id}
          onClose={hideModalDefinirEquipePadrao}
          onRemoverEquipePadrao={handleRemoverEquipePadrao}
          onSuccess={novoContato => {
            dispatch(updateListaContatos(novoContato));
            let contatoV2: ContatoInterfaceV2 = contato;

            if (novoContato.equipePadrao) {
              contatoV2 = {
                ...contatoV2,
                teamDefault: {
                  id: novoContato.equipePadrao.id,
                  name: novoContato.equipePadrao.nome,
                },
              };
            }

            if (novoContato.usuarioPadrao) {
              contatoV2 = {
                ...contatoV2,
                atendenteDefault: {
                  id: novoContato.usuarioPadrao.id,
                  name: novoContato.usuarioPadrao.nome,
                  descricao: novoContato.usuarioPadrao.apelido,
                  foto: novoContato.usuarioPadrao.foto,
                } as ContatoInterfaceV2['atendenteDefault'],
              };
            } else {
              contatoV2 = {
                ...contatoV2,
                atendenteDefault: null,
              };
            }

            dispatch(atualizarContatoV2(contatoV2));
          }}
        />
      );
    },
    [contato],
  );

  const [showModalIniciarContato, hideModalIniciarContato] = useModal(
    ({ in: open }) => {
      if (!contato) return null;

      const normalizedContato = normalizeContato(contato);

      return (
        <ModalIniciarContato
          open={open}
          contato={normalizedContato}
          onClose={() => {
            hideModalIniciarContato();
          }}
        />
      );
    },
    [contato],
  );

  const [showModalAddAgendamento, hideModalAddAgendamento] = useModal(
    ({ in: open }) => {
      if (!contato) return null;

      return (
        <ModalAddAgendamento
          open={open}
          onClose={hideModalAddAgendamento}
          contato={
            {
              id: contato?.id,
              nome: contato?.name,
              contato: contato?.contact,
              tipoCanal: contato?.tipoCanal,
            } as ContatoInterface
          }
        />
      );
    },
    [contato],
  );

  const displayPhone = useMemo(() => {
    if (!contato) return '';

    if (isInstagramOrMessengerChannel) return contato.contact;

    return phone || contato.contact;
  }, [contato, isInstagramOrMessengerChannel, phone]);

  const IconTipoCanal = contato
    ? getIconTipoCanal({
        tipoCanal: contato?.tipoCanal,
        isModoHibrido: contato.channel?.isHybridMode,
      })
    : null;
  const titleIniciarAtendimento = useMemo(() => {
    if (editMode)
      return 'Salve ou cancele a edição para iniciar um atendimento';
    if (contato?.blockedAt)
      return 'Contato bloqueado. Não é possível enviar mensagens.';
    return undefined;
  }, [editMode, contato?.blockedAt]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gridGap={16}
      style={{
        paddingBottom: 56,
      }}
    >
      {contato?.blockedAt && (
        <Box className={classes.blockedAlert}>
          <BlockIcon
            style={{
              color: theme.palette.error.main,
              fontSize: 20,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            style={{ color: theme.palette.error.main, fontWeight: 500 }}
          >
            Contato bloqueado. Não é possível enviar mensagens.
          </Typography>
        </Box>
      )}

      <Box display="flex" alignItems="center" gridGap={readonlyFields ? 8 : 12}>
        <Box className={classes.backgroundAvatar}>
          <Box className={classes.avatarBadge} style={{ position: 'relative' }}>
            <BlockedContactBadge isBlocked={Boolean(contato?.blockedAt)}>
              <ImageViewer src={contato?.fotoUrl}>
                <ContactAvatar
                  src={contato?.fotoUrl}
                  className={classes.avatarLarge}
                />
              </ImageViewer>
            </BlockedContactBadge>
          </Box>
        </Box>

        <ListItemText
          style={{ marginLeft: 16 }}
          primary={
            <Box component="span">
              {editMode && !readonlyFields ? (
                <TextField
                  name="name"
                  label="Nome"
                  variant="outlined"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  fullWidth
                  inputRef={el => {
                    if (inputRefs)
                      inputRefs.current.name = el as HTMLInputElement;
                  }}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  style={{ marginBottom: 8 }}
                  classes={{
                    root: classes.inputText,
                  }}
                />
              ) : (
                <Box
                  className={clsx(classes.nameContainer, {
                    [classes.editableField]: !readonlyFields,
                  })}
                >
                  <BlockedContactName
                    name={contato?.name ?? ''}
                    isBlocked={Boolean(contato?.blockedAt)}
                    variant="body2"
                  />
                  {!readonlyFields && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        onEnableEditMode?.(true);

                        setTimeout(() => {
                          inputRefs.current.name?.focus();
                        }, 0);
                      }}
                    >
                      <EditIcon fontSize="small" color="disabled" />
                    </IconButton>
                  )}
                </Box>
              )}
            </Box>
          }
          secondary={
            <Box
              display="flex"
              alignItems="center"
              gridGap={4}
              component="span"
            >
              {IconTipoCanal}
              <Box component="span">{displayPhone}</Box>
            </Box>
          }
          primaryTypographyProps={{
            style: { fontWeight: 600 },
            component: 'div',
          }}
          secondaryTypographyProps={{
            component: 'div',
          }}
        />
      </Box>

      <Box display="flex" flexDirection="column" gridGap={4}>
        {(contato?.teamDefault || contato?.atendenteDefault) && (
          <Box
            display="flex"
            alignItems="center"
            gridGap={4}
            className={classes.editableField}
          >
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Atendente padrão:
            </Typography>
            <Typography variant="body2">
              {contato?.teamDefault?.name}
              {contato?.teamDefault && contato?.atendenteDefault && ' - '}
              {contato?.atendenteDefault?.name}
            </Typography>
            {(contato?.teamDefault || contato?.atendenteDefault) && (
              <IconButton
                size="small"
                onClick={handleRemoverEquipePadrao}
                aria-label="Remover atendente padrão"
              >
                <CloseIcon fontSize="small" color="disabled" />
              </IconButton>
            )}
          </Box>
        )}

        <Box display="flex" gridGap={8}>
          <Typography
            variant="body2"
            style={{ fontWeight: 500, flexShrink: 0, marginTop: 1 }}
          >
            Tags do contato:
          </Typography>
          <TagsV2
            tags={
              contato?.tags.map(tag => ({
                id: tag.id,
                nome: tag.name,
                cor: tag.color,
              })) ?? []
            }
            isContato
            atendimentoId={atendimentoSelecionado?.id}
            id={contato?.id}
          />
        </Box>
      </Box>

      <Box display="flex" gridGap={6}>
        <Button
          variant="contained"
          fullWidth
          className={classes.actionButton}
          onClick={showModalDefinirEquipePadrao}
          disabled={editMode}
        >
          Definir atendente
        </Button>
        {!isInstagramOrMessengerChannel && (
          <Button
            variant="contained"
            fullWidth
            className={classes.actionButton}
            onClick={showModalAddAgendamento}
            disabled={editMode || Boolean(contato?.blockedAt)}
          >
            Agendar contato
          </Button>
        )}

        {!isAtendimento && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={showModalIniciarContato}
            disabled={editMode || Boolean(contato?.blockedAt)}
            title={titleIniciarAtendimento}
          >
            Iniciar atendimento
          </Button>
        )}
      </Box>

      <Box style={{ paddingBlock: 4 }}>
        {editMode && !readonlyFields ? (
          <TextField
            multiline
            minRows={4}
            name="observation"
            label="Observação"
            value={formik.values.observation}
            onChange={formik.handleChange}
            inputRef={el => {
              if (inputRefs)
                inputRefs.current.observation = el as HTMLInputElement;
            }}
            error={
              formik.touched.observation && Boolean(formik.errors.observation)
            }
            helperText={formik.touched.observation && formik.errors.observation}
            variant="outlined"
            fullWidth
            classes={{
              root: classes.inputText,
            }}
          />
        ) : (
          <Box
            className={clsx(classes.observation, {
              [classes.editableField]: !readonlyFields,
            })}
          >
            <Box>
              <Typography variant="body2" color="textSecondary">
                Observação
              </Typography>
              <Typography variant="body2">
                {contato?.observation ?? ''}
              </Typography>
            </Box>

            {!readonlyFields && (
              <IconButton
                size="small"
                onClick={() => {
                  onEnableEditMode?.(true);

                  setTimeout(() => {
                    inputRefs.current.observation?.focus();
                  }, 0);
                }}
              >
                <EditIcon fontSize="small" color="disabled" />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      {contato?.customFields && contato.customFields.length > 0 && (
        <Box display="flex" flexDirection="column" gridGap={8}>
          <FormikProvider value={formik}>
            {(editMode ||
              !readonlyFields ||
              contato.customFields.some(
                field => field.type === CustomFieldsEnum.LOGICAL,
              )) && (
              <Typography
                variant="body2"
                color="primary"
                style={{ fontWeight: 600, opacity: 0.7 }}
              >
                Campos personalizados
              </Typography>
            )}

            <CustomFields
              customFields={contato?.customFields ?? []}
              editMode={editMode}
              readonlyFields={readonlyFields}
              onEnableEditMode={onEnableEditMode}
              inputRefs={inputRefs}
            />
          </FormikProvider>
        </Box>
      )}

      <Slide direction="up" in={editMode} mountOnEnter unmountOnExit>
        <Box className={classes.formActions}>
          <Button
            onClick={() => {
              formik.resetForm();
              onEnableEditMode?.(false);
              onSuccess?.();
            }}
          >
            Cancelar
          </Button>
          <FormButton
            disabled={formik.isSubmitting}
            loading={formik.isSubmitting}
            variant="contained"
            color="primary"
            onClick={() => formik.handleSubmit()}
          >
            Salvar edição
          </FormButton>
        </Box>
      </Slide>
    </Box>
  );
}
